import { scrubObservability, type ObservabilityPolicy } from "@alchema/shared";
import { LOGGER_CONTEXT_POLICY } from "~/lib/security/observability-policy";

const errorCodes = [
  "unknown", "timeout", "network", "rate_limited", "upstream_rejected", "validation", "parse_failed",
  "provider_failed", "gateway_failed", "circuit_open", "retry_exhausted", "configuration_missing",
] as const;
const routes = ["home", "pricing", "jobs", "job_detail", "resumes", "resume_detail", "applications", "blog", "auth", "settings", "other"] as const;

const stackFramePolicy: ObservabilityPolicy = {
  fields: {
    lineno: { kind: "number", integer: true, min: 1, max: 10_000_000 },
    colno: { kind: "number", integer: true, min: 0, max: 1_000_000 },
    in_app: { kind: "boolean" },
  },
};

const exceptionValuePolicy: ObservabilityPolicy = {
  fields: {
    type: {
      kind: "enum",
      values: ["Error", "TypeError", "RangeError", "SyntaxError", "ObservabilityDiagnosticError", "UnknownError"],
    },
    stacktrace: {
      kind: "object",
      policy: {
        fields: {
          frames: {
            kind: "array",
            item: { kind: "object", policy: stackFramePolicy },
            maxItems: 64,
          },
        },
      },
    },
  },
};

const sentryPolicy: ObservabilityPolicy = {
  fields: {
    level: { kind: "enum", values: ["fatal", "error", "warning", "log", "info", "debug"] },
    platform: { kind: "enum", values: ["javascript", "node"] },
    exception: {
      kind: "object",
      policy: {
        fields: {
          values: { kind: "array", item: { kind: "object", policy: exceptionValuePolicy }, maxItems: 8 },
        },
      },
    },
    tags: {
      kind: "object",
      policy: {
        fields: {
          error_code: { kind: "enum", values: errorCodes },
          route: { kind: "enum", values: routes },
        },
      },
    },
    extra: { kind: "object", policy: LOGGER_CONTEXT_POLICY },
  },
};

export interface SentrySafeConfiguration {
  environment?: unknown;
  release?: unknown;
}

function safeEnvironment(value: unknown): string | undefined {
  return typeof value === "string" && ["development", "test", "preview", "production"].includes(value)
    ? value
    : undefined;
}

function safeRelease(value: unknown): string | undefined {
  return typeof value === "string" && /^[A-Za-z0-9][A-Za-z0-9._-]{0,79}$/.test(value)
    ? value
    : undefined;
}

/** Construct a new minimal Sentry event; disallowed raw event fields never copy. */
export function scrubSentryEvent(
  event: unknown,
  configuration: SentrySafeConfiguration = {},
): Record<string, unknown> {
  // Two extra bounded levels are required for exception.values[].stacktrace.frames[].
  // The frame policy retains only numbers/booleans and still uses the global
  // key, array, string, and total-output budgets.
  const safe = scrubObservability(event, sentryPolicy, { maxDepth: 6 });
  safe.type = "error";
  const environment = safeEnvironment(configuration.environment);
  const release = safeRelease(configuration.release);
  if (environment) safe.environment = environment;
  if (release) safe.release = release;

  const exception = safe.exception as { values?: Array<Record<string, unknown>> } | undefined;
  if (exception?.values) {
    exception.values = exception.values
      .filter((value) => typeof value.type === "string")
      .map((value) => ({
        type: value.type,
        value: "captured_operational_error",
        ...(value.stacktrace ? { stacktrace: value.stacktrace } : {}),
      }));
  }

  const errorCode = (safe.tags as Record<string, unknown> | undefined)?.error_code;
  if (typeof errorCode === "string") safe.fingerprint = [`alchema-${errorCode}`];
  return safe;
}
